import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/siteInfo';

export default function AboutPage() {
  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.fullName,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '望京SOHO T1 18层',
      addressLocality: '北京',
      addressRegion: '北京',
      addressCountry: 'CN',
    },
    foundingDate: siteInfo.foundingYear,
    sameAs: [siteInfo.social.weibo],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>

      <Navbar />

      <main className="pt-20">
        <div className="bg-background-50 border-b border-background-200">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Hero */}
        <section className="relative py-20 md:py-28 bg-background-50 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20abstract%20flowing%20golden%20light%20rays%20against%20soft%20cream%20background%2C%20minimalist%20style%2C%20inspirational%20and%20hopeful%20atmosphere%2C%20gentle%20gradients%2C%20ethereal%20composition%2C%20no%20text%20no%20people&width=1920&height=600&seq=about-hero-2026&orientation=landscape"
              alt=""
              className="w-full h-full object-cover opacity-50"
            />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto text-center px-4 md:px-6">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 mb-5">
              关于<span className="text-primary-600">人人出彩</span>
            </h1>
            <p className="text-sm md:text-lg text-foreground-500 leading-relaxed">
              {siteInfo.brandStory}
            </p>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              <div className="bg-background-50 rounded-2xl p-8 md:p-10">
                <div className="w-14 h-14 rounded-2xl bg-primary-100 flex items-center justify-center mb-6">
                  <i className="ri-flag-line text-primary-600 text-2xl"></i>
                </div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">我们的使命</h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {siteInfo.mission}
                </p>
              </div>
              <div className="bg-background-50 rounded-2xl p-8 md:p-10">
                <div className="w-14 h-14 rounded-2xl bg-accent-100 flex items-center justify-center mb-6">
                  <i className="ri-eye-line text-accent-600 text-2xl"></i>
                </div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">我们的愿景</h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {siteInfo.vision}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                我们的团队
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                一群有梦想的人，正在为更多普通人的梦想而努力
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {siteInfo.teamMembers.map((member) => (
                <div
                  key={member.name}
                  className="bg-background-100 rounded-2xl p-6 text-center transition-all duration-300 hover:translate-y-[-4px]"
                >
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-secondary-200 flex items-center justify-center">
                    <span className="font-heading text-2xl font-bold text-secondary-600">
                      {member.name.charAt(0)}
                    </span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-1">
                    {member.name}
                  </h3>
                  <p className="text-sm text-primary-600 font-medium mb-3">{member.role}</p>
                  <p className="text-xs text-foreground-500 leading-relaxed">{member.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 md:py-20 bg-secondary-900 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20geometric%20pattern%20with%20subtle%20texture%20and%20soft%20flowing%20lines%2C%20dark%20background%2C%20minimalist%20style%2C%20elegant%20atmosphere&width=1920&height=500&seq=about-stats-bg-2026&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-3">
                人人出彩 · 数据见证
              </h2>
              <p className="text-sm md:text-base text-background-50/60">
                每一个数字背后，都是无数普通人不凡故事的汇聚
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {[
                { value: siteInfo.stats.totalUsers, label: '注册用户' },
                { value: siteInfo.stats.totalStories, label: '出彩故事' },
                { value: siteInfo.stats.monthlyActive, label: '月活跃用户' },
                { value: siteInfo.stats.totalCategories, label: '内容分类' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-2">
                    {stat.value}
                  </p>
                  <p className="text-sm text-background-50/55">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                用户口碑
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                听听人人出彩的用户们怎么说
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {siteInfo.testimonials.map((t) => (
                <div
                  key={t.name}
                  className="bg-background-100 rounded-2xl p-6 md:p-8"
                >
                  <div className="flex items-center gap-1 mb-4 text-primary-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
                    "{t.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                      <span className="font-heading text-sm font-bold text-accent-600">
                        {t.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.title}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                如果你有任何问题、建议或合作意向，欢迎随时联系我们
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-background-50 rounded-2xl p-8 md:p-10">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-6">联系方式</h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</p>
                      <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-500 hover:text-primary-600 transition-colors">
                        {siteInfo.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900 mb-1">联系电话</p>
                      <p className="text-sm text-foreground-500">{siteInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900 mb-1">办公地址</p>
                      <p className="text-sm text-foreground-500">{siteInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-2xl p-8 md:p-10">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-6">发送消息</h3>
                <form
                  data-readdy-form="true"
                  action="https://readdy.ai/api/form/d9srept8o1vn02ekul20"
                  method="POST"
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                      placeholder="请输入您的消息（最多500字）"
                    ></textarea>
                  </div>
                  <div className="hp-field-wrapper">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <button
                    type="submit"
                    className="whitespace-nowrap w-full py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    发送消息
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}